import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useGetPersonByIdQuery, useUpdatePersonSafeMutation } from "@/services/api";
import type { ActivitiesTabFormValues } from "../types";
import { activitiesTabSchema } from "../schema";

export const useActivitiesTab = (isEditMode: boolean) => {
  const { personId } = useParams();

  const { data: person, isFetching } = useGetPersonByIdQuery(
    { id: personId ?? "" },
    {
      skip: !personId || personId === "TEMP_ID",
    },
  );
  const updatePerson = useUpdatePersonSafeMutation();

  const form = useForm<ActivitiesTabFormValues>({
    resolver: yupResolver(activitiesTabSchema),
  });

  useEffect(() => {
    if (person && personId !== "TEMP_ID") {
      form.reset({
        professionalActivities: person.professionalActivities,
        careers: person.careers,
        educations:
          person.educations.map((item) => ({
            ...item,
            place: item.school,
            additional: item.about,
          })) ?? [],
        competences: person.competences,
        hobbies: person.hobbies,
      });
    } else {
      form.reset({
        professionalActivities: [],
        careers: [],
        educations: [],
        competences: [],
        hobbies: [],
      });
    }
  }, [personId, isFetching, isEditMode]);

  const handleSubmit = (data: ActivitiesTabFormValues) => {
    if (!person) return;

    updatePerson({
      ...person,
      professionalActivities:
        data.professionalActivities?.map((activity) => ({
          id: activity.id ?? undefined,
          personId: personId ?? "",
          activity: activity.activity ?? "",
          role: activity.role ?? "",
          industry: { name: activity.industry?.name ?? "" },
        })) ?? [],
      careers:
        data.careers?.map((career) => ({
          id: career.id ?? undefined,
          personId: personId ?? "",
          company: career.company ?? "",
          jobTitle: career.jobTitle ?? "",
          industry: { name: career.industry?.name ?? "" },
          about: career.about ?? "",
          careerStart: career.careerStart ?? "",
          careerEnd: career.careerEnd ?? "",
        })) ?? [],
      educations:
        data.educations?.map((education) => ({
          id: education.id ?? undefined,
          personId: personId ?? "",
          school: education.place ?? "",
          major: education.major ?? "",
          about: education.additional ?? "",
          educationStart: education.educationStart ?? "",
          educationEnd: education.educationEnd ?? "",
        })) ?? [],
      competences:
        data.competences?.map((competence) => ({
          id: competence.id ?? undefined,
          personId: personId ?? "",
          about: competence.about ?? "",
          competenceType: { name: competence.competenceType?.name ?? "" },
        })) ?? [],
      hobbies:
        data.hobbies?.map((hobby) => ({
          personId: personId ?? "",
          about: hobby.about ?? "",
          hobbyType: { name: hobby.hobbyType?.name ?? "" },
        })) ?? [],
    }).then(() => {
      form.reset(
        {
          ...person,
          ...data,
        },
        {
          keepValues: true,
        },
      );
    });
  };

  return {
    state: {
      form,
      person,
      isLoading: isFetching,
      personId,
      addDisabled: personId === "TEMP_ID",
    },
    methods: { handleSubmit },
  };
};
